import { createContext, useContext } from "react";
import type {
  EmailVerificationStatus,
  PendingEmailVerificationStatus,
  PendingSignupResult,
} from "../firebase";

export type { PendingEmailVerificationStatus } from "../firebase";

export interface AuthUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  customClaims?: Record<string, unknown>;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /** A bounded Google redirect-return error suitable for authentication UIs. */
  authError?: string | null;
  /**
   * True when resend can be authorized by either the current BFF session or
   * the same-tab transient Firebase user created during pending sign-up.
   */
  canResendVerificationEmail?: boolean;
  signIn: () => Promise<void>;
  signInWithEmail?: (email: string, password: string) => Promise<void>;
  /** Provider label only; public/legal fields and current BFF snapshot stay unchanged.
   * Explicit ordinary sign-in is required to refresh the session-visible label. */
  updateAccountName?: (name: string, password: string) => Promise<void>;
  signUpWithEmail?: (
    email: string,
    password: string,
    displayName?: string,
    returnUrl?: string,
  ) => Promise<PendingEmailVerificationStatus>;
  /**
   * Sign-up that does NOT establish a web session (#1307). Creates the account
   * and sends the verification email, leaving the new user as the Firebase
   * current-user (so `resendVerificationEmail` works) but unauthenticated in the
   * app — the caller routes to a "verify your email" screen. The user gains a
   * session by signing in after verifying. Returning logins are never gated.
   */
  signUpPendingVerification?: (
    email: string,
    password: string,
    displayName?: string,
    returnUrl?: string,
  ) => Promise<PendingSignupResult>;
  /** Re-send using the transient Firebase user or authenticated BFF session. */
  resendVerificationEmail?: (
    returnUrl?: string,
  ) => Promise<EmailVerificationStatus>;
  resetPassword?: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
